export type GreekPortMapping = {
  /** The "All Ports" code (e.g., ATH, JTR00, C00) */
  allPortsCode: string;
  /** Common names users might use */
  aliases: string[];
  /** Official port name */
  officialName: string;
};

/**
 * The real, individually bookable ports that make up "Athens (all ports)" / ATH.
 * search_trips does NOT reliably aggregate across these when given the ATH code
 * (e.g. it misses Rafina-only routes like Andros/Tinos), so callers that search
 * ATH need to also fan out to these and merge results.
 */
export const ATHENS_SUB_PORT_CODES = ['PIR', 'RAF', 'LAV'];

export const GREEK_PORTS_MAP: Record<string, GreekPortMapping> = {
  // Athens - Special case (ATH not ATH00)
  'athens': {
    allPortsCode: 'ATH',
    aliases: ['athens', 'athina', 'piraeus', 'pireaus', 'pireas'],
    officialName: 'Athens (all ports)',
  },

  // Santorini
  'santorini': {
    allPortsCode: 'JTR00',
    aliases: ['santorini', 'thira', 'thera', 'fira'],
    officialName: 'Santorini (all ports)',
  },

  // Crete
  'crete': {
    allPortsCode: 'C00',
    aliases: ['crete', 'kriti', 'heraklion', 'chania', 'rethymno'],
    officialName: 'Crete (all ports)',
  },

  // Mykonos
  'mykonos': {
    allPortsCode: 'JMK00',
    aliases: ['mykonos', 'myconos'],
    officialName: 'Mykonos (all ports)',
  },

  // Paros
  'paros': {
    allPortsCode: 'PAS',
    aliases: ['paros', 'paroikia'],
    officialName: 'Paros',
  },

  // Naxos
  'naxos': {
    allPortsCode: 'JNX',
    aliases: ['naxos'],
    officialName: 'Naxos',
  },

  // Rhodes
  'rhodes': {
    allPortsCode: 'RHO00',
    aliases: ['rhodes', 'rodos'],
    officialName: 'Rhodes (all ports)',
  },

  // Ios
  'ios': {
    allPortsCode: 'IOS00',
    aliases: ['ios'],
    officialName: 'Ios (all ports)',
  },

  // Kos
  'kos': {
    allPortsCode: 'KO00',
    aliases: ['kos'],
    officialName: 'Kos (all ports)',
  },

  // Samos
  'samos': {
    allPortsCode: 'SMS',
    aliases: ['samos', 'vathi', 'vathy'],
    officialName: 'Samos (all ports)',
  },

  // Lesvos/Mytilene
  'lesvos': {
    allPortsCode: 'LES00',
    aliases: ['lesvos', 'lesbos', 'mytilene', 'mitilini'],
    officialName: 'Lesvos (all ports)',
  },

  // Chios
  'chios': {
    allPortsCode: 'CH00',
    aliases: ['chios', 'hios'],
    officialName: 'Chios (all ports)',
  },

  // Corfu
  'corfu': {
    allPortsCode: 'CFU00',
    aliases: ['corfu', 'kerkyra', 'korfu'],
    officialName: 'Corfu (all ports)',
  },

  // Zakynthos
  'zakynthos': {
    allPortsCode: 'ZTH00',
    aliases: ['zakynthos', 'zante'],
    officialName: 'Zakynthos (all ports)',
  },

  // Kefalonia
  'kefalonia': {
    allPortsCode: 'KE00',
    aliases: ['kefalonia', 'cephalonia', 'kefallonia'],
    officialName: 'Kefalonia (all ports)',
  },

  // Lefkada
  'lefkada': {
    allPortsCode: 'LEF00',
    aliases: ['lefkada', 'lefkas', 'levkas'],
    officialName: 'Lefkada (all ports)',
  },

  // Milos
  'milos': {
    allPortsCode: 'MLO',
    aliases: ['milos', 'melos'],
    officialName: 'Milos',
  },

  // Syros
  'syros': {
    allPortsCode: 'JSY00',
    aliases: ['syros', 'siros', 'ermoupoli'],
    officialName: 'Syros (all ports)',
  },

  // Tinos
  'tinos': {
    allPortsCode: 'TIN00',
    aliases: ['tinos'],
    officialName: 'Tinos',
  },

  // Andros
  'andros': {
    allPortsCode: 'AND',
    aliases: ['andros'],
    officialName: 'Andros',
  },

  // Sifnos
  'sifnos': {
    allPortsCode: 'SIF',
    aliases: ['sifnos'],
    officialName: 'Sifnos',
  },

  // Serifos
  'serifos': {
    allPortsCode: 'SER',
    aliases: ['serifos'],
    officialName: 'Serifos',
  },

  // Folegandros
  'folegandros': {
    allPortsCode: 'FOL00',
    aliases: ['folegandros'],
    officialName: 'Folegandros',
  },

  // Sikinos
  'sikinos': {
    allPortsCode: 'SIK',
    aliases: ['sikinos'],
    officialName: 'Sikinos',
  },

  // Amorgos
  'amorgos': {
    allPortsCode: 'AMR',
    aliases: ['amorgos'],
    officialName: 'Amorgos',
  },

  // Skiathos
  'skiathos': {
    allPortsCode: 'JSI',
    aliases: ['skiathos'],
    officialName: 'Skiathos',
  },

  // Skopelos
  'skopelos': {
    allPortsCode: 'SK00',
    aliases: ['skopelos'],
    officialName: 'Skopelos',
  },

  // Alonissos
  'alonissos': {
    allPortsCode: 'ALO',
    aliases: ['alonissos', 'alonnisos'],
    officialName: 'Alonissos',
  },
};

/**
 * Fast lookup function that prefers "All Ports" codes
 */
export function getPreferredPortCode(destination: string): string | null {
  const normalized = destination.trim().toLowerCase();

  // Direct match
  if (GREEK_PORTS_MAP[normalized]) {
    return GREEK_PORTS_MAP[normalized].allPortsCode;
  }

  // Search aliases
  for (const [key, mapping] of Object.entries(GREEK_PORTS_MAP)) {
    if (mapping.aliases.some(alias => normalized.includes(alias) || alias.includes(normalized))) {
      return mapping.allPortsCode;
    }
  }

  return null;
}

/**
 * Reverse lookup: Get display name from port code
 * Returns the official name for "all ports" codes, or null if not found
 */
export function getPortDisplayName(portCode: string): string | null {
  if (!portCode) return null;

  const normalizedCode = portCode.trim().toUpperCase();

  // Search through the map to find matching code
  for (const [key, mapping] of Object.entries(GREEK_PORTS_MAP)) {
    if (mapping.allPortsCode === normalizedCode) {
      return mapping.officialName;
    }
  }

  return null;
}

/**
 * Merges a raw port list (fetched live from the Ferryhopper MCP) with the curated
 * GREEK_PORTS_MAP entries.
 *
 * - "All ports" islands (officialName contains "(all ports)", e.g. Mykonos) can have
 *   several distinct real piers — those raw entries are kept as-is, and only the
 *   curated aggregate entry itself is added/deduped, so nothing is lost.
 * - Single-port islands (no "(all ports)" suffix, e.g. Paros) have exactly one real
 *   port, so any raw entry for that island (matched by name OR alias) is dropped in
 *   favor of the single curated entry — no point listing the same pier twice.
 *
 * Aliases are only used for the single-port exclusion: a multi-port island's aliases
 * (e.g. "piraeus" under "athens") must NOT remove raw entries, because Piraeus is
 * itself a distinct, individually bookable port that has to stay in the list.
 */
export function mergeWithGreekPortsMap(
  rawPorts: Array<{ name: string; code: string }>
): Array<{ name: string; code: string }> {
  const mappings = Object.values(GREEK_PORTS_MAP);
  const isMultiPort = (m: GreekPortMapping) => m.officialName.toLowerCase().includes('all ports');

  // Strips a trailing "(all ports)" so a raw entry like "SIFNOS (ALL PORTS)" is
  // recognized as the same island as our curated "Sifnos" entry, even though the
  // curated one no longer carries that suffix once we've marked it single-port.
  const stripAllPorts = (name: string) =>
    name.trim().toLowerCase().replace(/\s*\(all ports\)\s*$/i, '').trim();

  const singlePortNames = new Set<string>();
  for (const m of mappings) {
    if (isMultiPort(m)) continue;
    singlePortNames.add(m.officialName.toLowerCase());
    for (const alias of m.aliases) {
      singlePortNames.add(alias.toLowerCase());
    }
  }

  const multiPortOfficialNames = new Set(
    mappings.filter(isMultiPort).map(m => m.officialName.toLowerCase())
  );

  const curatedCodes = new Set(mappings.map(m => m.allPortsCode.toUpperCase()));

  const filtered = rawPorts.filter(p => {
    const nameLower = p.name.trim().toLowerCase();
    const codeUpper = p.code.trim().toUpperCase();
    if (curatedCodes.has(codeUpper)) return false;
    if (singlePortNames.has(stripAllPorts(p.name))) return false;
    if (multiPortOfficialNames.has(nameLower)) return false;
    return true;
  });

  const curated = mappings.map(m => ({ name: m.officialName, code: m.allPortsCode }));

  return [...filtered, ...curated];
}
